import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '@/components/feature/SearchBar';
import { heroSlides } from '@/mocks/homeContent';
import { getWorkById, TYPE_META } from '@/lib/works';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = heroSlides.length;

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const goTo = (next: number) => setIndex((next + total) % total);
  const active = heroSlides[index];
  const activeWork = getWorkById(active.workId);

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0">
        {heroSlides.map((slide, slideIndex) => (
          <img
            key={slide.id}
            src={slide.image}
            alt={`${slide.title} 中文字幕推荐`}
            title={`${slide.title} - 日韩中文字幕`}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${
              slideIndex === index ? 'opacity-100 animate-slow-zoom' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/40"></div>
      </div>

      <div className="relative px-4 pb-20 pt-32 md:px-8 md:pb-24 md:pt-40 lg:px-10">
        <div className="mx-auto w-full max-w-[1240px]">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-400/50 bg-accent-500/15 px-3.5 py-1.5 text-xs font-medium text-accent-300">
            <i className="ri-flashlight-line"></i>
            {active.kicker}
          </span>

          <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            日韩中文字幕
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            最新日韩影视作品中文字幕资源汇总，电影 · 电视剧 · 综艺一站查找，助你轻松了解字幕版本与更新进度。
          </p>

          <div className="mt-6 max-w-xl">
            <SearchBar size="lg" />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50/15 text-background-50">
                <i className={activeWork ? TYPE_META[activeWork.type].icon : 'ri-movie-line'}></i>
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-accent-300">当前焦点</p>
                <h2 className="font-heading text-base font-bold text-background-50">
                  {active.title}
                  <span className="ml-2 text-xs font-normal text-background-300">
                    {active.originalTitle}
                  </span>
                </h2>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.workId}`}
                className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                查看详情
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to={activeWork ? `/category/${activeWork.type}` : '/category/movie'}
                className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/15"
              >
                浏览分类
              </Link>
            </div>
          </div>

          <p className="mt-5 max-w-2xl text-sm text-background-200">{active.tagline}</p>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-6 px-4 md:px-8 lg:px-10">
        <div className="mx-auto flex w-full max-w-[1240px] items-center gap-3">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => goTo(index - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/35 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => goTo(index + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/35 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
          <div className="ml-2 flex items-center gap-2">
            {heroSlides.map((slide, slideIndex) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`切换到第 ${slideIndex + 1} 张`}
                onClick={() => goTo(slideIndex)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  slideIndex === index ? 'w-8 bg-accent-400' : 'w-3 bg-background-50/40 hover:bg-background-50/70'
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}